import type { SightedCamera } from '@hotlist/shared';

/**
 * What you have found by driving.
 *
 * DESIGN.md section 8, and the one thing in this game the civilian side earns
 * rather than receives. Enforcement is handed its own coverage because
 * operators have it. Everybody else starts blind and buys the map a mile at a
 * time, in the open, on roads the network is watching them drive.
 *
 * Two properties, and the second is the interesting one:
 *
 * - It never fades. A plate is gone from memory in three minutes because the
 *   car drove away. A pole cannot drive away, so going back to look at it again
 *   is not a feat of memory, it is a fact about a pole.
 * - It survives the browser closing, because the county is persistent and a map
 *   that reset every session would make discovery busywork rather than
 *   investment.
 *
 * It is also, deliberately, **wrong over time**. A camera taken down last week
 * is still on here, and one that went up yesterday is not, and nothing corrects
 * that except going and looking. That staleness is the same staleness the real
 * crowdsourced maps have, for the same reason, and it is worth having rather
 * than papering over.
 */

const STORAGE_KEY = 'hotlist.cameras';

/**
 * The map is the plate's, not the person's.
 *
 * Section 4: the plate is the identity. Somebody who buys a second car has not
 * driven past those junctions in it, and the network has no idea the two cars
 * are the same person -- which is the whole of why a second plate is a second
 * life. A found map shared across a keyring would quietly undo that.
 *
 * The server keys its sightings the same way and is the source of truth. This
 * is a cache so the map can draw before the connection answers.
 */
function keyFor(plate: string): string {
  return plate ? `${STORAGE_KEY}.${plate}` : STORAGE_KEY;
}

export interface KnownCamera extends SightedCamera {
  /** Wall-clock milliseconds when this was first seen. */
  found: number;
  /** Wall-clock milliseconds when it was last laid eyes on. */
  confirmed: number;
}

export class KnownCameras {
  private readonly byId = new Map<string, KnownCamera>();
  private dirty = false;
  private plate = '';

  constructor(plate = '') {
    this.plate = plate;
    this.load();
  }

  /**
   * Change which car's map this is.
   *
   * Switching cars is a reconnection, and what the new plate has seen comes
   * back from the server on seating. This swaps the cache to match so the map
   * does not show the other car's knowledge for the second it takes.
   */
  setPlate(plate: string): void {
    if (plate === this.plate) return;
    this.plate = plate;
    this.byId.clear();
    this.load();
  }

  private load(): void {
    try {
      const raw = localStorage.getItem(keyFor(this.plate));
      if (raw) {
        const held = JSON.parse(raw) as KnownCamera[];
        let dropped = 0;
        for (const camera of held) {
          if (!camera || typeof camera.id !== 'string') continue;
          // A bearing of -1 cannot come from this server.
          //
          // It is the archive's no-bearing sentinel, and it used to be passed
          // through to the client, which drew every one of those cones due
          // north. The server now computes the direction from the approach, so
          // -1 is an impossible value -- which makes it an exact marker for an
          // entry written by the old code, and those entries are worth
          // dropping rather than keeping.
          //
          // They are the 811 locate tickets. Eight filings for one Sussex dig
          // were being read as eight cameras, so a map saved before that was
          // fixed holds seven poles that were never there, stacked on one
          // junction, inflating the count and the avoid list. Those are not
          // stale in the way section 8 wants a found map to be stale -- that is
          // about the world moving on, and this is about the game having been
          // wrong. Anything real here comes straight back on the next drive
          // past, with a direction on it this time.
          //
          // This is self-limiting: nothing written from now on can match it.
          if (typeof camera.b === 'number' && camera.b < 0) {
            dropped++;
            continue;
          }
          this.byId.set(camera.id, camera);
        }
        // Written back now rather than waiting for the next sighting, which
        // may never come: a player who parks and reads their week should not
        // still have seven phantom cameras on disk.
        if (dropped > 0) this.save();
      }
    } catch {
      // A corrupt map is not worth failing a boot over. You go and look again.
    }
  }

  get size(): number {
    return this.byId.size;
  }

  all(): KnownCamera[] {
    return [...this.byId.values()];
  }

  has(id: string): boolean {
    return this.byId.has(id);
  }

  /** Everything found, as edge ids, which is what a route avoids. */
  watchedEdges(): number[] {
    return [...new Set(this.all().map((camera) => camera.e))];
  }

  /**
   * Fold in what was just seen.
   *
   * Re-seeing a camera updates `confirmed` and its live state but never
   * `found`, because when you first learned about it is the part worth keeping.
   */
  add(seen: SightedCamera[]): number {
    const now = Date.now();
    let fresh = 0;
    for (const camera of seen) {
      const held = this.byId.get(camera.id);
      if (held) {
        held.confirmed = now;
        held.down = camera.down === true;
        held.x = camera.x;
        held.y = camera.y;
      } else {
        this.byId.set(camera.id, { ...camera, found: now, confirmed: now });
        fresh++;
      }
      this.dirty = true;
    }
    if (this.dirty) this.save();
    return fresh;
  }

  private save(): void {
    try {
      localStorage.setItem(keyFor(this.plate), JSON.stringify(this.all()));
      this.dirty = false;
    } catch {
      // Out of quota, or storage disabled. The map is still good for this
      // session and saying so is not worth a dialog.
    }
  }

  /** Throw the map away. Used by nothing yet; here because a player may want to. */
  forget(): void {
    this.byId.clear();
    this.save();
  }
}

/**
 * The cameras an agency operates, while you are on shift.
 *
 * Deliberately **not** the same object as what a plate has found, and this is
 * the whole reason it exists separately. §8 gives the two sides different
 * knowledge on purpose: an operator knows where their own poles are, and
 * everybody else earns the map a mile at a time on roads the network is
 * watching them drive. If signing on for one shift permanently added your
 * department's deployment to the map you drive around as a civilian, the
 * civilian half of the game would be over.
 *
 * So: never persisted, never merged, and emptied the moment the shift ends.
 * There is no localStorage key here and that is not an oversight.
 */
export class Coverage {
  private cameras: SightedCamera[] = [];

  set(seen: readonly SightedCamera[]): void {
    this.cameras = [...seen];
  }

  clear(): void {
    this.cameras = [];
  }

  all(): readonly SightedCamera[] {
    return this.cameras;
  }

  get size(): number {
    return this.cameras.length;
  }
}
